import type { Account, DashboardData } from "../types";
import type { PriceObservation } from "../types/receipt";
import { accountNeed, categorizeNeed, statementNeed, tripNeed, tripsNeed, widestNeed, type Need } from "../lib/coverage";
import { todayIn } from "../lib/finance";
import type { View } from "./views";

/**
 * What the screen being shown needs beyond the recent months, which only the loaded payload's
 * digests can say: how far back a card's statement, an account's latest entries, a trip's entries or
 * a tracked price's entry reach. Null when the recent months are enough. Screens whose need depends
 * on something opened inside them (a payee, a loan, a bill) ask for it themselves.
 */
export function extraLedgerNeed({ view, data, reviewUncategorized, tripFilter, priceRows, month }: {
  view: View;
  month?: string;
  data: DashboardData | null;
  reviewUncategorized: boolean;
  tripFilter: string;
  priceRows: PriceObservation[];
}): Need | null {
  if (!data) return null;
  const trips = data.trips || [];
  const forView: Record<string, () => Need | null> = {
    accounts: () => month && month < todayIn(data.timezone).slice(0, 7) ? null : statementNeed(data.accounts),
    overview: () => tripsNeed(data, trips.filter((t) => t.status === "active").map((t) => t.id)),
    trips: () => tripsNeed(data, trips.map((t) => t.id)),
    transactions: () => widestNeed([reviewUncategorized ? categorizeNeed(data) : null, tripFilter !== "All" ? tripNeed(data, tripFilter) : null]),
    budgets: () => tripsNeed(data, trips.filter((t) => (t.status === "active" || t.status === "planned") && t.budget !== null).map((t) => t.id)),
    achievements: () => statementNeed(data.accounts),
    prices: () => widestNeed(priceRows.map((p) => p.transactionDate ? p.transactionDate.slice(0, 7) : null)),
  };
  return forView[view]?.() ?? null;
}

/** What an open account's detail needs on top of the page behind it: its latest entries, and a card's statement cycle. */
export const accountDetailNeed = (data: DashboardData | null, account: Account | null): Need | null =>
  data && account ? widestNeed([accountNeed(data, account.id), statementNeed([account])]) : null;
